import React from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { AggregatedEvent } from '../../types/calendarTypes';
import { getStatusColor } from '../../utils/calendarUtils';
import CalendarEventComponent from './CalendarEventComponent';

interface MobileWeekAgendaProps {
    date: Date;
    events: AggregatedEvent[];
    onSelectEvent: (event: AggregatedEvent) => void;
}

// Mobile-only replacement for react-big-calendar's native week grid. A
// 7-column grid is too narrow to show useful shift details on a phone, so
// instead we stack each day full-width and let the user scroll vertically -
// each day renders the same detailed event cards used in day view.
const MobileWeekAgenda: React.FC<MobileWeekAgendaProps> = ({ date, events, onSelectEvent }) => {
    const weekStart = moment(date).startOf('week');
    const days = Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'days'));
    const today = moment();

    return (
        <Box sx={{ height: '100%', overflowY: 'auto', pb: 2 }}>
            {days.map((day) => {
                const dayEvents = events
                    .filter((e) => moment(e.start).isSame(day, 'day'))
                    .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());
                const isToday = day.isSame(today, 'day');

                return (
                    <Box key={day.format('YYYY-MM-DD')} sx={{ mb: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 1,
                                px: 1.5,
                                py: 1,
                                borderBottom: '1px solid #e0e0e0',
                                backgroundColor: isToday ? '#fff5f5' : 'transparent',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#CC0000', fontSize: '1rem' }}>
                                {day.format('ddd')}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: isToday ? 700 : 500, color: isToday ? '#CC0000' : 'text.secondary', fontSize: '0.9rem' }}
                            >
                                {day.format('MMM D')}
                            </Typography>
                        </Box>

                        {dayEvents.length === 0 ? (
                            <Typography variant="body2" sx={{ px: 1.5, py: 1.5, color: 'text.disabled', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                No shifts
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1.5, pt: 1 }}>
                                {dayEvents.map((event) => (
                                    <Box
                                        key={event.id}
                                        onClick={() => onSelectEvent(event)}
                                        sx={{
                                            backgroundColor: getStatusColor(event.resource.status),
                                            borderRadius: '6px',
                                            opacity: 0.95,
                                            color: 'black',
                                            p: 1.25,
                                            cursor: 'pointer',
                                            '&:active': { opacity: 0.8 },
                                        }}
                                    >
                                        <CalendarEventComponent event={event} variant="detailed" calendarView="week" />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default MobileWeekAgenda;